/* Image URLs come from current Shopify collection.image, never the saved navigation data.
 * The separate model handle projection joins already admitted canonical identities only.
 */
(() => {
  const modelHandlesUrl = document.currentScript?.dataset.modelHandlesUrl;
  const images = new Map(), requests = new Map();
  let modelHandlesPromise;
  const validHandle = handle => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(handle);
  const root = window.Shopify?.routes?.root || '/';
  const request = handle => {
    if (!validHandle(handle)) return Promise.reject(new Error('COLLECTION_IMAGE_HANDLE_INVALID'));
    if (!requests.has(handle)) requests.set(handle, (async () => {
      const url = new URL(`${root}collections/${encodeURIComponent(handle)}`, location.origin);
      url.searchParams.set('view', 'current-images');
      // Shopify negotiates its standard collection JSON for application/json;
      // request the Liquid view, whose body is our current-image JSON document.
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`COLLECTION_IMAGES_HTTP_${response.status}`);
      const data = await response.json();
      if (data.schemaVersion !== 'hoonies-current-collection-images.v1' || data.handle !== handle || !data.children || typeof data.children !== 'object') throw new Error('COLLECTION_IMAGES_INVALID');
      const entries = [[handle, data.image], ...Object.entries(data.children)];
      for (const [key, value] of entries) {
        if (!validHandle(key) || !(value === null || typeof value === 'string')) throw new Error('COLLECTION_IMAGE_INVALID');
      }
      for (const [key, value] of entries) images.set(key, value || '');
      return data;
    })());
    return requests.get(handle);
  };
  const load = async (handles, { parent = 'makes' } = {}) => {
    const wanted = [...new Set(handles)].filter(validHandle);
    try {
      await request(parent);
      const missing = wanted.filter(handle => !images.has(handle));
      let position = 0;
      await Promise.all(Array.from({ length: Math.min(2, missing.length) }, async () => {
        while (position < missing.length) await request(missing[position++]);
      }));
    } catch (error) {
      // A media failure must not remove vehicle choices or change product membership.
      console.warn('[current-collection-images]', error);
    }
    return Object.fromEntries(wanted.map(handle => [handle, images.get(handle) || '']));
  };
  const modelHandles = () => {
    if (!modelHandlesPromise) modelHandlesPromise = (async () => {
      if (!modelHandlesUrl) throw new Error('MODEL_COLLECTION_HANDLES_UNAVAILABLE');
      const response = await fetch(modelHandlesUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`MODEL_COLLECTION_HANDLES_HTTP_${response.status}`);
      const data = await response.json();
      if (data.schemaVersion !== 'hoonies-model-collection-handles.v1' || !data.models) throw new Error('MODEL_COLLECTION_HANDLES_INVALID');
      return data.models;
    })();
    return modelHandlesPromise;
  };
  const category = async tree => {
    const makeImages = await load(tree.map(make => make.k));
    const params = new URLSearchParams(location.search);
    const selected = params.get('vehicle_make_key') || decodeURIComponent(location.pathname).match(/\/collections\/([^/]+)/)?.[1] || '';
    const make = tree.find(row => row.k === selected);
    const modelImages = {}, collectionHandles = {};
    if (make) {
      try {
        const mapping = await modelHandles();
        for (const model of make.m) {
          const handle = mapping[model.id];
          if (validHandle(handle)) collectionHandles[model.id] = handle;
        }
        const current = await load(Object.values(collectionHandles), { parent: make.k });
        for (const [id, handle] of Object.entries(collectionHandles)) modelImages[id] = current[handle] || '';
      } catch (error) {
        console.warn('[current-collection-images]', error);
      }
    }
    return { makeImages, modelImages, collectionHandles };
  };
  window.hooniesCollectionImages = { load, category };
})();
